/**
 * CostSummaryCard - Card de resumo de custos de rota
 *
 * Exibe custos calculados:
 * - Total geral
 * - Breakdown por categoria
 * - Custo por km e por parada
 * - Comparativo com estimativa anterior (opcional)
 */

import { useMemo, cloneElement, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  Chip,
  Collapse,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  LocalGasStation as FuelIcon,
  Person as DriverIcon,
  Build as MaintenanceIcon,
  Toll as TollIcon,
  MoreHoriz as OtherIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Speed as SpeedIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import {
  calculatePlanCost,
  calculateRouteCost,
  formatCurrency,
  loadCostConfig,
} from '../services/costCalculationService';

/**
 * Componente de linha de custo individual
 */
const CostLine = ({ icon, label, value, color, tokens, percentage = null }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      py: 0.75,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon && cloneElement(icon, { sx: { fontSize: 16, color: color || tokens?.text?.tertiary } })}
      <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
        {label}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {percentage !== null && (
        <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px' }}>
          ({percentage}%)
        </Typography>
      )}
      <Typography sx={{ color: tokens?.text?.primary, fontSize: '13px', fontWeight: 500 }}>
        {formatCurrency(value)}
      </Typography>
    </Box>
  </Box>
);

/**
 * Componente de metrica de custo
 */
const CostMetric = ({ label, value, icon, tokens }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      p: 1,
      flex: 1,
      backgroundColor: tokens?.surface?.base,
      borderRadius: tokens?.radius?.sm,
    }}
  >
    {icon && cloneElement(icon, { sx: { fontSize: 16, color: tokens?.text?.tertiary, mb: 0.5 } })}
    <Typography sx={{ color: tokens?.text?.primary, fontSize: '14px', fontWeight: 600 }}>
      {value}
    </Typography>
    <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px' }}>
      {label}
    </Typography>
  </Box>
);

const CostSummaryCard = ({
  routes = [],
  singleRoute = null,
  costConfig = null,
  showBreakdown = true,
  showMetrics = true,
  onConfigClick,
  compact = false,
  title = 'Custos Estimados',
}) => {
  const tokens = usePremiumTokens();
  const [expanded, setExpanded] = useState(!compact);

  // Usar config passada ou carregar do storage
  const config = costConfig || loadCostConfig();

  // Calcular custos
  const costData = useMemo(() => {
    if (singleRoute) {
      return {
        type: 'single',
        data: calculateRouteCost(singleRoute, config),
      };
    }
    if (routes.length > 0) {
      return {
        type: 'plan',
        data: calculatePlanCost(routes, config),
      };
    }
    return null;
  }, [routes, singleRoute, config]);

  // Se nao ha dados, nao renderizar
  if (!costData) {
    return null;
  }

  const summary = costData.type === 'single'
    ? costData.data.summary
    : costData.data.totals;

  const routeInfo = costData.type === 'single'
    ? costData.data.route
    : {
        distanceKm: costData.data.totals.distanceKm,
        durationHours: costData.data.totals.durationHours,
        stopsCount: costData.data.totals.stopsCount,
      };

  // Calcular porcentagens para o breakdown
  const total = summary.totalCost || 1;
  const percentages = {
    fuel: Math.round((summary.fuelCost / total) * 100),
    driver: Math.round((summary.driverCost / total) * 100),
    maintenance: Math.round((summary.maintenanceCost / total) * 100),
    tolls: Math.round((summary.tollsCost / total) * 100),
    other: Math.round((summary.otherCosts / total) * 100),
  };

  return (
    <Box
      sx={{
        backgroundColor: tokens?.surface?.raised,
        borderRadius: tokens?.radius?.md,
        border: `1px solid ${tokens?.border?.subtle}`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: tokens?.surface?.elevated,
          cursor: compact ? 'pointer' : 'default',
        }}
        onClick={compact ? () => setExpanded(!expanded) : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon sx={{ color: tokens?.semantic?.success }} />
          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: tokens?.text?.primary }}>
            {title}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              color: tokens?.semantic?.success,
              fontWeight: 700,
              fontSize: '18px',
            }}
          >
            {formatCurrency(summary.totalCost)}
          </Typography>

          {onConfigClick && (
            <Tooltip title="Configurar custos">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onConfigClick();
                }}
                sx={{ color: tokens?.text?.tertiary }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {compact && (
            <IconButton size="small" sx={{ color: tokens?.text?.tertiary }}>
              {expanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={expanded}>
        {/* Metricas */}
        {showMetrics && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              p: 2,
              backgroundColor: tokens?.surface?.elevated,
              borderTop: `1px solid ${tokens?.border?.subtle}`,
            }}
          >
            <CostMetric
              label="por km"
              value={formatCurrency(summary.costPerKm || 0)}
              icon={<SpeedIcon />}
              tokens={tokens}
            />
            <CostMetric
              label="por parada"
              value={formatCurrency(summary.costPerStop || 0)}
              icon={<PlaceIcon />}
              tokens={tokens}
            />
            {costData.type === 'plan' && (
              <CostMetric
                label="veiculos"
                value={costData.data.totals.vehiclesCount}
                icon={<MoneyIcon />}
                tokens={tokens}
              />
            )}
          </Box>
        )}

        {/* Breakdown */}
        {showBreakdown && (
          <Box sx={{ p: 2 }}>
            <Typography
              sx={{
                color: tokens?.text?.tertiary,
                fontSize: '11px',
                fontWeight: 600,
                mb: 1,
              }}
            >
              DETALHAMENTO
            </Typography>

            <CostLine
              icon={<FuelIcon />}
              label="Combustivel"
              value={summary.fuelCost}
              color={tokens?.semantic?.warning}
              tokens={tokens}
              percentage={percentages.fuel}
            />

            <CostLine
              icon={<DriverIcon />}
              label="Motorista"
              value={summary.driverCost}
              color={tokens?.accent?.primary}
              tokens={tokens}
              percentage={percentages.driver}
            />

            {summary.maintenanceCost > 0 && (
              <CostLine
                icon={<MaintenanceIcon />}
                label="Manutencao"
                value={summary.maintenanceCost}
                tokens={tokens}
                percentage={percentages.maintenance}
              />
            )}

            {summary.tollsCost > 0 && (
              <CostLine
                icon={<TollIcon />}
                label="Pedagios"
                value={summary.tollsCost}
                color={tokens?.semantic?.info}
                tokens={tokens}
                percentage={percentages.tolls}
              />
            )}

            {summary.otherCosts > 0 && (
              <CostLine
                icon={<OtherIcon />}
                label="Outros"
                value={summary.otherCosts}
                tokens={tokens}
                percentage={percentages.other}
              />
            )}

            <Divider sx={{ my: 1, borderColor: tokens?.border?.subtle }} />

            {/* Barra de proporcao */}
            <Box sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', mb: 1 }}>
              <Box
                sx={{
                  width: `${percentages.fuel}%`,
                  backgroundColor: tokens?.semantic?.warning,
                }}
              />
              <Box
                sx={{
                  width: `${percentages.driver}%`,
                  backgroundColor: tokens?.accent?.primary,
                }}
              />
              <Box
                sx={{
                  width: `${percentages.maintenance}%`,
                  backgroundColor: tokens?.text?.tertiary,
                }}
              />
              <Box
                sx={{
                  width: `${percentages.tolls}%`,
                  backgroundColor: tokens?.semantic?.info,
                }}
              />
              <Box
                sx={{
                  width: `${percentages.other}%`,
                  backgroundColor: tokens?.surface?.elevated,
                }}
              />
            </Box>

            {/* Legenda compacta */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={`Combustivel ${percentages.fuel}%`}
                sx={{
                  height: 20,
                  fontSize: '10px',
                  backgroundColor: `${tokens?.semantic?.warning}20`,
                  color: tokens?.semantic?.warning,
                }}
              />
              <Chip
                size="small"
                label={`Motorista ${percentages.driver}%`}
                sx={{
                  height: 20,
                  fontSize: '10px',
                  backgroundColor: `${tokens?.accent?.primary}20`,
                  color: tokens?.accent?.primary,
                }}
              />
            </Box>
          </Box>
        )}

        {/* Info da rota */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
            pt: 0,
            color: tokens?.text?.tertiary,
            fontSize: '12px',
          }}
        >
          <span>{routeInfo.distanceKm?.toFixed(1)} km</span>
          <span>{routeInfo.durationHours?.toFixed(1)} horas</span>
          <span>{routeInfo.stopsCount} paradas</span>
        </Box>
      </Collapse>
    </Box>
  );
};

export default CostSummaryCard;
